import { CircleInfoIcon } from "@xola/icons";
import { ComboBox, FormGroup, Label, Tooltip } from "@xola/ui-kit";
import React, { useEffect, useMemo, useState } from "react";
import { useLocalization } from "../../hooks/useLocalization";
import { Language } from "../../models/Locale";
import { useLocalizationStore } from "../../stores/localizationStore";

interface LanguageSelectorProps {
    label?: string;
    tooltip?: string;
    value?: string;
    onChange: (value?: string) => void;
}

export const LanguageSelector = ({ label, tooltip, value, onChange }: LanguageSelectorProps) => {
    const { initialize } = useLocalization();
    const [initialized, setInitialized] = useState<boolean>(!!window.Localize);

    const availableLanguages = useLocalizationStore((state) => state.availableLanguages);

    const selectedValue = useMemo(() => {
        return availableLanguages.find((lang) => lang.code === value);
    }, [value, availableLanguages]);

    useEffect(() => {
        if (!initialized) {
            /**
             * We don't have "Localize" instance available; this happens when a user's current locale is "en_US" only.
             * So no localize.js script would have been loaded on the application startup
             **/
            initialize().then(() => setInitialized(true));
            return;
        }

        if (availableLanguages.length === 0) {
            /**
             * We have "Localize" instance available; but no languages are available yet. So we need to fetch available localize languages
             */
            void initialize();
        }
    }, []);

    if (!initialized) {
        return null;
    }

    return (
        <FormGroup>
            {label && (
                <Label>
                    {label}

                    {tooltip && (
                        <Tooltip content={tooltip}>
                            <CircleInfoIcon size="small" className="ml-1" />
                        </Tooltip>
                    )}
                </Label>
            )}
            <ComboBox
                isClearable={false}
                isSearchable={false}
                options={availableLanguages}
                getOptionValue={(language: Language) => language.code}
                getOptionLabel={(language: Language) => language.name}
                value={selectedValue}
                classNames={{ option: () => "no-translate" }}
                onChange={(language: Language) => onChange(language.code)}
            />
        </FormGroup>
    );
};
